/**
 * Shared flavored-markdown reference grammar (§4.10 / §4.15).
 *
 * One tokenizer, `scanRefs`, drives BOTH description rendering and
 * standalone `.md` documents. It recognizes:
 *
 *   - `[[#id]]`            element ref by stable id
 *   - `[[Name]]`           element ref by (qualified) name
 *   - `[[@@key]]` / `[[@@k.path:value]]`  aspect ref (`.` = key segments, `:` = value)
 *   - `[[Doc#section]]`    document-section ref
 *   - `@path`               FIELD getter interpolation (description-only)
 *   - `@@path`              ASPECT getter interpolation (description-only)
 *   - `[[ref]]@path`        foreign FIELD getter / `[[ref]]@@path` foreign ASPECT
 *                          getter (a ref immediately followed by a getter reads
 *                          the field/aspect off the *referenced* node — falls out
 *                          of the token stream as a ref token adjacent to a getter
 *                          token)
 *
 * §5.1 / spec §4.10: the axis is spelled by the sigil — `@` = field (value axis),
 * `@@` = aspect. `@@` munches before `@` (the regex tries the two-`@` sigil first).
 *
 * Code regions (fenced blocks + inline spans) are split off first via
 * `splitCodeRegions` and never tokenized — extensions inside code are
 * literal text.
 */

// =============================================================================
// Code-region splitting
// =============================================================================

export interface Segment {
  readonly kind: "text" | "code" | "calc";
  readonly text: string;
  /** calc only: `={ … }` is precise (2 sig figs); `~{ … }` is back-of-envelope. */
  readonly precise?: boolean;
}

/**
 * Split input into `text`, `code`, and `calc` segments. Code segments pass
 * through verbatim; `calc` segments are `~{ … }` estimate regions handled by the
 * calculator. Recognized code regions: fenced blocks (``` / ~~~) and inline code
 * spans (`` `…` ``). Both code and calc shield their contents from `[[…]]`/`@`.
 */
export function splitCodeRegions(input: string): Segment[] {
  const segs: Segment[] = [];
  let i = 0;
  let textStart = 0;

  const flushText = (end: number): void => {
    if (end > textStart) {
      segs.push({ kind: "text", text: input.slice(textStart, end) });
    }
  };

  while (i < input.length) {
    // Fenced code block: line starts with ``` or ~~~ (allow leading ws).
    if (i === 0 || input[i - 1] === "\n") {
      const fence = matchFence(input, i);
      if (fence) {
        flushText(i);
        const end = findFenceEnd(input, fence.end, fence.marker);
        segs.push({ kind: "code", text: input.slice(i, end) });
        i = end;
        textStart = end;
        continue;
      }
    }
    // Calc region: `~{ … }` (estimate) or `={ … }` (precise). Single- or
    // multi-line; calc never uses `}`.
    if ((input[i] === "~" || input[i] === "=") && input[i + 1] === "{") {
      const close = input.indexOf("}", i + 2);
      if (close !== -1) {
        flushText(i);
        segs.push({ kind: "calc", text: input.slice(i, close + 1), precise: input[i] === "=" });
        i = close + 1;
        textStart = i;
        continue;
      }
      // No closer — treat as text.
    }
    // Inline code span: count backticks.
    if (input[i] === "`") {
      const runStart = i;
      while (input[i] === "`") i++;
      const tickCount = i - runStart;
      const closer = findInlineCodeClose(input, i, tickCount);
      if (closer !== -1) {
        flushText(runStart);
        segs.push({ kind: "code", text: input.slice(runStart, closer) });
        i = closer;
        textStart = closer;
        continue;
      }
      // No closer — treat the run as text.
      continue;
    }
    i++;
  }
  flushText(input.length);
  return segs;
}

function matchFence(input: string, i: number): { end: number; marker: string } | undefined {
  let j = i;
  while (j < input.length && (input[j] === " " || input[j] === "\t")) j++;
  const ch = input[j];
  if (ch !== "`" && ch !== "~") return undefined;
  let k = j;
  while (input[k] === ch) k++;
  if (k - j < 3) return undefined;
  return { end: k, marker: input.slice(j, k) };
}

function findFenceEnd(input: string, from: number, marker: string): number {
  let i = from;
  while (i < input.length) {
    const nl = input.indexOf("\n", i);
    const lineEnd = nl === -1 ? input.length : nl + 1;
    let j = i;
    while (j < input.length && (input[j] === " " || input[j] === "\t")) j++;
    if (input.startsWith(marker, j)) {
      let k = j + marker.length;
      while (input[k] === marker[0]) k++;
      if (k - j >= marker.length) {
        let trail = k;
        while (trail < input.length && input[trail] !== "\n") trail++;
        return trail < input.length ? trail + 1 : trail;
      }
    }
    i = lineEnd;
  }
  return input.length;
}

function findInlineCodeClose(input: string, from: number, tickCount: number): number {
  let i = from;
  while (i < input.length) {
    if (input[i] === "`") {
      const start = i;
      while (input[i] === "`") i++;
      if (i - start === tickCount) return i;
    } else {
      i++;
    }
  }
  return -1;
}

// =============================================================================
// Inline reference tokenizer
// =============================================================================

/** `[[ … ]]` — any non-`]`, non-newline run. */
export const REF_RE = /\[\[([^\]\n]+)\]\]/g;
/**
 * A getter interpolation `@path` (field) or `@@path` (aspect), preceded by a
 * char that is neither an identifier char nor `@` (so `foo@bar`, `foo@@bar` and
 * a stray second `@` of a `@@` are not mistaken for a bare getter). The sigil is
 * captured in group 2 so consumers can tell the value axis; `@@` wins over `@`.
 */
// Path atoms mirror the lexer's unicode identifier shape — an ASCII-only class
// left `@версия`-style getters untokenized, rendering the literal sigil-path
// the spec forbids (r46). `\p{ID_Continue}` covers ASCII [A-Za-z0-9_], so the
// preceding-char guard stays equivalent for ASCII text.
export const GETTER_RE =
  /(^|[^@\p{ID_Continue}])(@@?)([\p{ID_Start}_][\p{ID_Continue}]*(?:\.[\p{ID_Start}_][\p{ID_Continue}]*)*)/gu;

/** The sigil of a getter interpolation — `@` reads a field, `@@` an aspect.
 *  Not exported: `GetterSigil` is exported canonically from `selector-ast.ts`
 *  (the barrel would otherwise re-export the name twice — TS2308). Consumers
 *  import it from there; this local alias just types the tokens below. */
type GetterSigil = "@" | "@@";

export type RefToken =
  | { readonly kind: "text"; readonly text: string }
  | {
      /** A `[[ … ]]` reference. `inner` is trimmed; classify with `classifyRef`. */
      readonly kind: "ref";
      readonly raw: string;
      readonly inner: string;
      readonly index: number;
    }
  | {
      /** A bare `@path` (field) / `@@path` (aspect) getter interpolation. The
       *  `sigil` names the value axis; `path` excludes the sigil. */
      readonly kind: "getter";
      readonly raw: string;
      readonly path: string;
      readonly sigil: GetterSigil;
      /** Offset of the (first) `@`. */
      readonly index: number;
    };

/**
 * Tokenize one already-code-stripped text segment into an ordered stream of
 * text / ref / getter tokens. A `@`/`@@` that falls inside a `[[ … ]]` span is
 * part of the ref (an aspect ref), never a separate getter token. The char
 * preceding a bare getter (required by the grammar) is left in the text stream,
 * so adjacent `[[ref]]@path` naturally emits a ref token followed by a getter token.
 */
export function scanRefs(text: string): RefToken[] {
  const refs: { start: number; end: number; raw: string; inner: string }[] = [];
  REF_RE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = REF_RE.exec(text)) !== null) {
    refs.push({ start: m.index, end: m.index + m[0].length, raw: m[0], inner: (m[1] ?? "").trim() });
  }

  const getters: { atIndex: number; end: number; raw: string; path: string; sigil: GetterSigil }[] = [];
  GETTER_RE.lastIndex = 0;
  while ((m = GETTER_RE.exec(text)) !== null) {
    const before = m[1] ?? "";
    const sigil = (m[2] ?? "@") as GetterSigil;
    const path = m[3] ?? "";
    const atIndex = m.index + before.length; // position of the (first) '@'
    if (refs.some((r) => atIndex >= r.start && atIndex < r.end)) continue; // inside a ref
    getters.push({ atIndex, end: m.index + m[0].length, raw: `${sigil}${path}`, path, sigil });
  }

  type Ev =
    | { kind: "ref"; start: number; end: number; raw: string; inner: string }
    | { kind: "getter"; start: number; end: number; raw: string; path: string; sigil: GetterSigil };
  const events: Ev[] = [
    ...refs.map((r) => ({ kind: "ref" as const, start: r.start, end: r.end, raw: r.raw, inner: r.inner })),
    ...getters.map((g) => ({
      kind: "getter" as const,
      start: g.atIndex,
      end: g.end,
      raw: g.raw,
      path: g.path,
      sigil: g.sigil,
    })),
  ].sort((a, b) => a.start - b.start);

  const out: RefToken[] = [];
  let cursor = 0;
  for (const e of events) {
    if (e.start < cursor) continue; // defensive: skip any overlap
    if (e.start > cursor) out.push({ kind: "text", text: text.slice(cursor, e.start) });
    if (e.kind === "ref") out.push({ kind: "ref", raw: e.raw, inner: e.inner, index: e.start });
    else out.push({ kind: "getter", raw: e.raw, path: e.path, sigil: e.sigil, index: e.start });
    cursor = e.end;
  }
  if (cursor < text.length) out.push({ kind: "text", text: text.slice(cursor) });
  return out;
}

// =============================================================================
// Reference classification
// =============================================================================

export type ClassifiedRef =
  | { readonly kind: "id"; readonly id: string }
  | { readonly kind: "name"; readonly name: string }
  | { readonly kind: "aspect"; readonly key: string; readonly value?: string }
  | { readonly kind: "section"; readonly doc: string; readonly section: string };

/**
 * Classify a `[[ … ]]` inner string (§4.10 / §5.1):
 *   `#id`             → element by id
 *   `@@key` / `@@k:v` → aspect (`.` segments the key, `:` introduces a value)
 *   `Doc#section`     → a section of a document
 *   `Name`            → element by name
 *
 * The single-`@` `[[@key]]` spelling is retired (aspect refs are now `[[@@key]]`).
 * It is no longer a valid ref form, so it falls through to a (doomed) `name` ref
 * and surfaces as an unresolved reference rather than silently resolving — there
 * is no migration path (pre-release, §5.1).
 */
export function classifyRef(inner: string): ClassifiedRef {
  if (inner.startsWith("#")) {
    return { kind: "id", id: inner.slice(1).trim() };
  }
  if (inner.startsWith("@@")) {
    const body = inner.slice(2);
    const colon = body.indexOf(":");
    if (colon === -1) return { kind: "aspect", key: body.trim() };
    return { kind: "aspect", key: body.slice(0, colon).trim(), value: body.slice(colon + 1).trim() };
  }
  const hash = inner.indexOf("#");
  if (hash !== -1) {
    return { kind: "section", doc: inner.slice(0, hash).trim(), section: inner.slice(hash + 1).trim() };
  }
  return { kind: "name", name: inner.trim() };
}
